// A limit on the body's markdown length (a Case's context field). In a live
// field the save is a Yjs merge on the server and cannot be refused (two people
// typing at once may go over without losing text). Instead the screen stops the
// person's own typing from going over.
//
// Counted like the server: the length of the markdown string. Remote changes
// (y-sync) are never stopped - the Y.Doc and the view would diverge. A change
// that shortens or keeps the length passes even over the limit (so one can
// delete back under it).

import { Extension } from "@tiptap/core";
import type { Node as PMNode } from "@tiptap/pm/model";
import { Plugin, PluginKey, type Transaction } from "@tiptap/pm/state";
import { serializeMarkdown } from "./markdown";

export const markdownLengthGuardKey = new PluginKey("trameriaMarkdownLength");

export function createMarkdownLengthGuard(
  max: number,
  isRemote: (tr: Transaction) => boolean,
) {
  // Lengths by document (the previous doc is not serialized again per keystroke).
  const lengths = new WeakMap<PMNode, number>();
  const lengthOf = (doc: PMNode) => {
    const known = lengths.get(doc);
    if (known !== undefined) return known;
    const length = serializeMarkdown(doc).length;
    lengths.set(doc, length);
    return length;
  };
  return new Plugin({
    key: markdownLengthGuardKey,
    filterTransaction(tr, state) {
      if (!tr.docChanged || isRemote(tr)) return true;
      const next = lengthOf(tr.doc);
      if (next <= max) return true;
      return next <= lengthOf(state.doc);
    },
  });
}

/** {@link createMarkdownLengthGuard} as an extension. */
export const MarkdownLengthGuard = Extension.create<{
  max: number;
  isRemote: (tr: Transaction) => boolean;
}>({
  name: "markdownLengthGuard",
  addOptions() {
    return { max: Number.POSITIVE_INFINITY, isRemote: () => false };
  },
  addProseMirrorPlugins() {
    return [createMarkdownLengthGuard(this.options.max, this.options.isRemote)];
  },
});
